import { useRouter } from '@tanstack/react-router';
import {
  Button,
  Flex,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import loadingImage from '@/assets/images/loading.svg';

// "Close", not "Cancel": dismissing the modal doesn't stop the queued export.
const DISMISS_LABEL = 'Close';

export type ExportProgressModalData = {
  workspaceId: number;
  /** The modal title, e.g. "Export People" (taxonomy-aware, computed by the caller). */
  title: string;
  /** null = starting, true = queued, false = failed (Ember's `lastTaskSuccess`). */
  lastTaskSuccess: boolean | null;
  /** The failure message when known (Ember's `lastError`) — gates the Retry button. */
  lastError: string | null;
  /** Re-runs the export that failed (the Retry button). */
  onRetry: () => void;
};

/**
 * Legacy fire-and-forget export terminal: "we'll email you" on success, retry
 * on failure. Ported from the Ember `ExportProgressModal`; on the people page
 * it serves as the fallback when the export POST fails (the live status modal
 * handles the happy path). The "Export other" flow (FPS-3664) reuses it as its
 * primary terminal UX.
 */
export const ExportProgressModal = ({ data, onClose, modalState }: PlumaManagedModalProps<ExportProgressModalData>) => {
  const { workspaceId, title, lastTaskSuccess, lastError, onRetry } = data;
  const router = useRouter();

  const exportsUrl = router.buildLocation({
    to: '/workspaces/$workspaceId/exports',
    params: { workspaceId },
  }).href;

  return (
    <Modal state={modalState} title={title} onClose={onClose}>
      <ModalBody>
        <Flex alignItems="center" justifyContent="center" p="250">
          <Image src={loadingImage} alt="" width="80px" height="80px" />
        </Flex>

        <Paragraph mb="200">
          {lastTaskSuccess === true ? (
            <>
              Your export is currently processing. When it is ready, you will receive an email, and it will be available
              for download on the{' '}
              <Link href={exportsUrl} variant="secondary" isUnderlined isExternal withSafeExternalAttributes={false}>
                exports page
              </Link>
              .
            </>
          ) : lastError != null ? (
            'There was a problem processing your export. Try again.'
          ) : (
            'We are starting your export now…'
          )}
        </Paragraph>
      </ModalBody>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          {DISMISS_LABEL}
        </Button>
        {lastTaskSuccess === true ? (
          <Button variant="primary" ml="100" onClick={onClose}>
            Done
          </Button>
        ) : lastError != null ? (
          <Button variant="primary" ml="100" onClick={onRetry}>
            Retry
          </Button>
        ) : (
          // Inert placeholder while the export POST is in flight; the onClick
          // never fires while disabled.
          <Button variant="primary" ml="100" isDisabled onClick={onClose}>
            Working…
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
